import React, { useState, useEffect } from 'react';
import { Box, Portal, useDisclosure } from '@chakra-ui/react';
import { Navigate, Route, Routes, useLocation, matchPath } from 'react-router-dom';
import Footer from 'components/footer/FooterAdmin.js';
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import { useAuth } from 'contexts/AuthContext';
import { canAccessRoute, getDefaultRoute } from 'utils/roleAccess';
import RequireAccess from 'components/auth/RequireAccess';
import UnauthorizedView from 'views/admin/UnauthorizedView';
import routes from 'routes.js';

const sidebarOffset = (collapsed) => (collapsed ? '70px' : '300px');

export default function Dashboard(props) {
  const { ...rest } = props;
  const location = useLocation();
  const { roles, accesses } = useAuth();
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const getActiveRoute = (routeList) => {
    let activeRoute = null;
    routeList.forEach((route) => {
      if (matchPath(route.layout + route.path, location.pathname)) {
        activeRoute = route;
      }
      if (route.items && route.items.length > 0) {
        activeRoute = getActiveRoute(route.items) || activeRoute;
      }
    });
    return activeRoute;
  };

  useEffect(() => {
    if (location.pathname === '/admin/unauthorized') return;
    const activeRoute = getActiveRoute(routes);
    if (activeRoute && !canAccessRoute(activeRoute, roles, accesses)) {
      window.location.replace('/admin/unauthorized');
    }
  }, [location.pathname, roles, accesses]);

  const getRoutes = (routeList) => {
    return routeList.flatMap((route, key) => {
      if (route.category) {
        return getRoutes(route.items);
      }
      if (route.layout === "/admin") {
        return (
          <Route
            path={route.path}
            element={
              <RequireAccess route={route}>
                {route.component}
              </RequireAccess>
            }
            key={key}
          />
        );
      }
      return [];
    });
  };

  document.documentElement.dir = 'ltr';
  const { onOpen } = useDisclosure();

  const activeRoute = getActiveRoute(routes);
  const showSearch = activeRoute?.showSearch;
  const offset = sidebarOffset(sidebarCollapsed);
  const defaultRoute = getDefaultRoute(roles, accesses);

  return (
    <Box>
      <SidebarContext.Provider value={{
        toggleSidebar,
        setToggleSidebar,
        sidebarCollapsed,
        setSidebarCollapsed,
      }}>
        <Sidebar routes={routes} display="none" collapsed={sidebarCollapsed} {...rest} />
        <Box
          ml={{ base: 0, xl: offset }}
          w={{ base: '100%', xl: `calc(100% - ${offset})` }}
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          transition="margin 0.3s ease, width 0.3s ease"
        >
          <Portal>
            <Box>
              <Navbar
                onOpen={onOpen}
                brandText={activeRoute ? activeRoute.name : 'Default Brand Text'}
                fixed={fixed}
                showSearch={showSearch}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                {...rest}
              />
            </Box>
          </Portal>

          <Box mx="auto" p={{ base: '20px', md: '30px' }} minH="94vh" pt={{ base: '100px', md: '110px' }}>
            <Routes>
              {getRoutes(routes)}
              <Route path="/unauthorized" element={<UnauthorizedView />} />
              <Route
                path="/"
                element={
                  <Navigate
                    to={defaultRoute.replace(/^\/admin\/?/, '') || 'dashboard'}
                    replace
                  />
                }
              />
            </Routes>
          </Box>
          <Box>
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}
